import { retryFetch } from "@caredna-marlin/utils/client";

const base64URLEncode = (str: string): string => {
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
};

const generateCodeVerifier = (): string => {
  const array = new Uint8Array(32);
  window.crypto.getRandomValues(array);
  const verifier = Array.from(array)
    .map((byte) => String.fromCharCode(byte))
    .join("");
  return base64URLEncode(verifier);
};

const generateCodeChallenge = async (
  verifier: string
): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const hashed = await window.crypto.subtle.digest("SHA-256", data);
  const challenge = Array.from(new Uint8Array(hashed))
    .map((byte) => String.fromCharCode(byte))
    .join("");
  return base64URLEncode(challenge);
};

export const fetchIdpByEmail = async (
  email: string
): Promise<string | null> => {
  const response = await retryFetch(() => fetch(
    import.meta.env.VITE_SERVICE_HOST + "/idp-lookup",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email }),
      credentials: "include",
    }
  ));
  const json = await response.json();
  const idp = json.provider;
  return idp;
};

type InitiateLoginOptions = {
  idp: string;
  clientId?: string | null;
  redirectUri?: string | null;
  codeChallenge?: string | null;
  state?: string | null;
};

export const initiateLogin = async (
  opts: InitiateLoginOptions
): Promise<void> => {
  try {
    const cognitoClientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
    const cognitoHost = import.meta.env.VITE_COGNITO_HOST;
    const redirectUri = encodeURIComponent(
      opts.redirectUri ||
        `${import.meta.env.VITE_WEB_HOST}/auth/cognito/callback`
    );
    const codeChallenge =
      opts.codeChallenge || (await generateAndSaveCodeChallenge());
    const idProvider = opts.idp;

    let loginUrl =
      `${cognitoHost}/oauth2/authorize?` +
      `response_type=code&client_id=${cognitoClientId}&redirect_uri=${redirectUri}` +
      `&code_challenge=${codeChallenge}&code_challenge_method=S256&identity_provider=${idProvider}`;

    if (opts.state) {
      loginUrl += `&state=${opts.state}`;
    }

    window.location.href = loginUrl;
  } catch (error) {
    console.error("Error during PKCE login initiation:", error);
  }
};

export const redirectToCognitoLogout = async() => {
  const cognitoClientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
  const cognitoHost = import.meta.env.VITE_COGNITO_HOST;
  const logoutUri = encodeURIComponent(import.meta.env.VITE_WEB_HOST + "/");

  const logoutUrl =
    `${cognitoHost}/logout?client_id=${cognitoClientId}&logout_uri=${logoutUri}`;

  window.location.assign(logoutUrl);
}

const generateAndSaveCodeChallenge = async (): Promise<string> => {
  // Generate the code_verifier and code_challenge
  const codeVerifier: string = generateCodeVerifier();
  const codeChallenge: string = await generateCodeChallenge(codeVerifier);

  // Store code_verifier in sessionStorage for later use in token exchange
  sessionStorage.setItem("pkce_code_verifier", codeVerifier);

  return codeChallenge;
};
